import React, { Component } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { H3, Tag, Card, Spinner, Callout } from "@blueprintjs/core";
import { Trans } from "react-i18next";

import {
  authenticatedGetFetch,
  authenticatedPostFetch,
} from "../../utils/AuthenticatedFetch";
import { updateUser } from "../Home/Home";

const widgets = [
  { id: "0", content: <Trans>distribution of equipment by operating status</Trans> },
  { id: "1", content: <Trans>summary</Trans> },
  { id: "2", content: <Trans>parks</Trans> },
  { id: "3", content: <Trans>counters</Trans> },
  { id: "4", content: <Trans>event flow</Trans> },
  { id: "5", content: <Trans>event history</Trans> },
  { id: "6", content: <Trans>manage subscribers</Trans> },
  { id: "7", content: <Trans>equipments' state</Trans> },
  { id: "8", content: <Trans>price calculation</Trans> },
  { id: "9", content: <Trans>remote I/O</Trans> },
];

function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

function move(source, destination, droppableSource, droppableDestination) {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);
  destClone.splice(droppableDestination.index, 0, removed);
  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;
  return result;
}

const GRID = 2;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the selected look a bit nicer
  userSelect: "none",
  padding: GRID * 2,
  margin: `0 0 ${GRID}px 0`,
  // change background colour if dragging
  background: isDragging ? "lightgreen" : "white",
  // styles we need to apply on draggables
  ...draggableStyle,
});

const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? "lightblue" : "lightgrey",
  padding: GRID,
  margin: "4px",
  minHeight: "80%",
});

export default class CustomSupEditor extends Component {
  constructor() {
    super();
    this.state = {
      selected: [],
      remaining: [],
    };
    this.id2List = {
      favorites: "selected",
      leftChoices: "remaining",
    };
  }

  componentDidMount() {
    const s = [];
    const r = [];
    authenticatedGetFetch("/customsup", (res) => {
      for (const w of widgets) {
        if (res.includes(parseInt(w.id))) s.push(w);
        else r.push(w);
      }
      this.setState({ selected: s, remaining: r });
    });
  }

  getList = (id) => this.state[this.id2List[id]];

  sendChoiceToServer(choices) {
    this.setState({ loading: true });
    authenticatedPostFetch(
      "/customsup",
      { favs: choices, page: "supervision" },
      () => {
        this.setState({ loading: false });
        if (updateUser !== undefined) updateUser();
      }
    );
  }

  onDragEnd = (result) => {
    const { source, destination } = result;
    // dropped outside the list
    if (!destination) return;

    // dropped on the same area, only the order changed
    if (source.droppableId === destination.droppableId) {
      const selected = reorder(
        this.getList(source.droppableId),
        source.index,
        destination.index
      );

      let state = { selected };

      if (source.droppableId === "leftChoices") state = { remaining: selected };

      this.setState(state);
      this.sendChoiceToServer(selected);
    } else {
      // dropped on the other area
      const result = move(
        this.getList(source.droppableId),
        this.getList(destination.droppableId),
        source,
        destination
      );

      this.setState({
        selected: result.favorites,
        remaining: result.leftChoices,
      });
      this.sendChoiceToServer(result.favorites);
    }
  };

  render() {
    return (
      <Callout title={<Trans>page customization</Trans>} icon="drive-time">
        <Card>
          <DragDropContext onDragEnd={this.onDragEnd}>
            <div className="flexbox-center" style={{ marginBottom: 15 }}>
              <div>
                <H3>
                  <Trans>your supervision page</Trans>
                </H3>
                <Droppable droppableId="favorites">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      style={getListStyle(snapshot.isDraggingOver)}
                    >
                      {this.state.selected.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                            >
                              {
                                <Tag fill={true} large={true}>
                                  {item.content}
                                </Tag>
                              }
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
              <div>
                <H3>
                  <Trans>available widgets</Trans>
                </H3>
                <Droppable droppableId="leftChoices">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      style={getListStyle(snapshot.isDraggingOver)}
                    >
                      {this.state.remaining.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                            >
                              {<Tag large={true}>{item.content}</Tag>}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            </div>
          </DragDropContext>
          {this.state.loading ? (
            <Callout intent="warning">
              <div className="flexbox-left">
                <Spinner size={20} />
                <Trans>syncronizing data with the server</Trans>
              </div>
            </Callout>
          ) : (
            <Callout intent="success">
              <Trans>data are syncronized with the server</Trans>
            </Callout>
          )}
        </Card>
      </Callout>
    );
  }
}
